import { GetPosParams, useGetPos } from "@bvhi/backend-api";
import { Alert, Card, Stack } from "@bvhi/ui-base";
import { ErrorIcon } from "@bvhi/ui-icons";
import { Suspense, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BVHINavigationCreateEntityButton } from "../../../../components/bvhi-navigation-create-entity-button";
import { NavigationBar } from "../../../../components/navigation-bar";
import { useAuthContext } from "../../../../context/use-auth-context";
import { useBVHIDataGridFilter } from "../../../../hooks/use-bvhi-datagrid-Filter";
import { useErrorObjectMapper } from "../../../../hooks/use-error-object-mapper";
import { Route } from "../../../../routes/_main/app/pos.index";
import { SortConfig } from "../../user/users/list/hooks/use-users-sort-params";
import { PosFilterCard } from "./components/pos-filter-card";
import { PosDataGridCard } from "./components/pos-datagrid-card";

export const PosListView = () => {
  const { t } = useTranslation(["pos"]);

  const getSortConfig = useCallback(() => {
    return {
      backendString: "NAME_ASC",
      dataGridSortModel: {
        field: "updatedAt",
        sort: "asc",
      },
    } as SortConfig;
  }, []);

  const { handleDataGridChange, requestParams, handleFilterChange, resetFilter } = useBVHIDataGridFilter<GetPosParams>(
    {},
    Route.fullPath,
    getSortConfig
  );
  const { currentUser } = useAuthContext();
  const { getErrorMessagesFromErrorObject } = useErrorObjectMapper();
  const { error } = useGetPos(currentUser?.organisation?.id ?? "", requestParams);

  const errorMessages = useMemo(() => {
    if (!error?.errors) return [];
    const errItems = getErrorMessagesFromErrorObject(error);
    return errItems.map((errItem) => (
      <Alert key={errItem} icon={<ErrorIcon />} severity="error">
        {errItem}
      </Alert>
    ));
  }, [error, getErrorMessagesFromErrorObject]);

  return (
    <>
      <NavigationBar
        actions={[<BVHINavigationCreateEntityButton key={0} entity={t("pos:pos.entity")} navigateTarget={{ to: "/app/pos/create" }} />]}
        showBreadcrumbs={true}
      />
      <Stack pt={2} spacing={2}>
        {errorMessages.map((errorComponent) => errorComponent)}
        <PosFilterCard filter={requestParams} onChange={handleFilterChange} onReset={resetFilter} />
        <Suspense fallback={<Card sx={{ height: 300, minHeight: 300 }} />}>
          <PosDataGridCard requestParams={requestParams} updateCallback={handleDataGridChange} />
        </Suspense>
      </Stack>
    </>
  );
};
